import React from "react"
import Box from "@material-ui/core/Box"
import Typography from "@material-ui/core/Typography"
import Grid from "@material-ui/core/Grid"
import Button from "@material-ui/core/Button"
import Table from "@material-ui/core/Table"
import TableBody from "@material-ui/core/TableBody"
import TableCell from "@material-ui/core/TableCell"
import TableHead from "@material-ui/core/TableHead"
import TableRow from "@material-ui/core/TableRow"

import Image from "./image"

export interface TrainingSummaryRow {
  level: string
  frequency: string
  focus: string
  weeklyHabit: string
}

export interface TrainingSection {
  title: string
  paragraphs: string[]
}

interface Props {
  sport: string
  title: string
  intro: string
  image: string
  imageAlt: string
  sharedCalendarUrl?: string
  summary: TrainingSummaryRow[]
  sections: TrainingSection[]
}

const sectionSpacing = {
  marginTop: 32,
}

const SportsTrainingPost = ({
  sport,
  title,
  intro,
  image,
  imageAlt,
  sharedCalendarUrl,
  summary,
  sections,
}: Props) => (
  <Box component="article" style={{ paddingTop: 40, paddingBottom: 56 }}>
    <Button color="primary" href="/guides/?group=Activity%20routines&category=Sports" style={{ marginBottom: 24 }}>
      Back to sports routines
    </Button>

    <Grid container spacing={4} alignItems="center">
      <Grid item xs={12} md={7}>
        <Typography variant="overline" color="primary">
          {sport} Training
        </Typography>
        <Typography variant="h3" component="h1" gutterBottom>
          {title}
        </Typography>
        <Typography variant="subtitle1" color="textSecondary">
          {intro}
        </Typography>
        {sharedCalendarUrl && (
          <Box style={{ marginTop: 20 }}>
            <Button
              variant="contained"
              color="primary"
              href={sharedCalendarUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open the shared weekly calendar
            </Button>
          </Box>
        )}
      </Grid>
      <Grid item xs={12} md={5}>
        <Image alt={imageAlt} filename={image} />
      </Grid>
    </Grid>

    <Box style={sectionSpacing}>
      <Typography variant="body1" paragraph>
        Use these routines as a general planning guide alongside coach advice,
        school commitments, and the athlete's age, sleep, and recovery. Training
        should feel purposeful, not packed for the sake of being busy.
      </Typography>
    </Box>

    <Box style={sectionSpacing}>
      <Typography variant="h5" component="h2" gutterBottom>
        Quick training summary
      </Typography>
      <Box style={{ overflowX: "auto" }}>
        <Table aria-label={`${sport} training routine summary`}>
          <TableHead>
            <TableRow>
              <TableCell>Stage</TableCell>
              <TableCell>Weekly rhythm</TableCell>
              <TableCell>Main focus</TableCell>
              <TableCell>Useful habit</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {summary.map(row => (
              <TableRow key={row.level}>
                <TableCell component="th" scope="row">
                  {row.level}
                </TableCell>
                <TableCell>{row.frequency}</TableCell>
                <TableCell>{row.focus}</TableCell>
                <TableCell>{row.weeklyHabit}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>

    {sections.map(section => (
      <Box style={sectionSpacing} key={section.title}>
        <Typography variant="h5" component="h2" gutterBottom>
          {section.title}
        </Typography>
        {section.paragraphs.map(paragraph => (
          <Typography variant="body1" paragraph key={paragraph}>
            {paragraph}
          </Typography>
        ))}
      </Box>
    ))}
  </Box>
)

export default SportsTrainingPost
